// components/customFunctionalComponents/components/SideCardProtocols.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Offcanvas, Accordion, ListGroup, Badge } from 'react-bootstrap';
import DOMPurify from 'dompurify';

export const SideCard = ({ show, onHide, protocoloData, placement = "end" }) => {
    const [width, setWidth] = useState(400);
    const isResizingRef = useRef(false);
    const initialPos = useRef(0);
    const initialWidth = useRef(0);

    const MIN_WIDTH_PX = 400;
    const MAX_WIDTH_VW = 35;

    const resize = useCallback((e) => {
        if (isResizingRef.current) {
            let newWidth;
            if (placement === "end") {
                newWidth = initialWidth.current + (initialPos.current - e.clientX);
            } else {
                newWidth = initialWidth.current + (e.clientX - initialPos.current);
            }

            const maxAllowedWidthPx = window.innerWidth * (MAX_WIDTH_VW / 100);
            newWidth = Math.max(MIN_WIDTH_PX, Math.min(newWidth, maxAllowedWidthPx));
            setWidth(newWidth);
        }
    }, [placement]);

    const stopResizing = useCallback(() => {
        isResizingRef.current = false;
        document.removeEventListener('mousemove', resize);
        document.removeEventListener('mouseup', stopResizing);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, [resize]);

    const startResizing = useCallback((e) => {
        e.preventDefault();
        isResizingRef.current = true;
        initialPos.current = e.clientX;
        initialWidth.current = width;

        document.addEventListener('mousemove', resize);
        document.addEventListener('mouseup', stopResizing);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'ew-resize';
    }, [width, resize, stopResizing]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', resize);
            document.removeEventListener('mouseup', stopResizing);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, [resize, stopResizing]);

    // ❗ Aquí va el return null, DESPUÉS de los hooks
    if (!protocoloData) return null;

    const getTipoIdName = (id) => {
        switch (Number(id)) {
            case 1: return 'Whatsapp';
            case 2: return 'Telegram';
            default: return 'Desconocido';
        }
    };

    const sanitizedEventoRiesgo = DOMPurify.sanitize(protocoloData.evento_riesgo || '');
    const sortedEscalamientos = protocoloData.escalamientos
        ? [...protocoloData.escalamientos].sort((a, b) => a.nivel - b.nivel)
        : [];

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement={placement}
            style={{ width: `${width}px` }}
            backdrop={false}
            enforceFocus={false}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    [placement === "end" ? 'left' : 'right']: 0,
                    width: '10px',
                    height: '100%',
                    cursor: 'ew-resize',
                    backgroundColor: 'rgba(38, 67, 105, 1)',
                    zIndex: 1000,
                }}
                onMouseDown={startResizing}
            />

            <Offcanvas.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
                <Offcanvas.Title style={{ fontSize: '1.5rem' }}>DETALLES DEL PROTOCOLO</Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body>
                <h4 className="mb-3 text-primary text-center text-uppercase" style={{ fontSize: '1.6rem' }}>
                    {protocoloData.titulo_protocolo}
                </h4>
                <p className="text-muted text-center" style={{ fontSize: '0.9rem' }}>
                    CEDIS: {protocoloData.cedisNombre}
                </p>

                <Accordion defaultActiveKey="0">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Evento de Riesgo</Accordion.Header>
                        <Accordion.Body>
                            {protocoloData.evento_riesgo ? (
                                <div
                                    className="evento-riesgo-content px-3"
                                    dangerouslySetInnerHTML={{ __html: sanitizedEventoRiesgo }}
                                />
                            ) : (
                                <p className="text-muted" style={{ fontSize: '0.95rem' }}>
                                    No hay descripción de evento de riesgo.
                                </p>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="1">
                        <Accordion.Header>Escalamientos ({sortedEscalamientos.length})</Accordion.Header>
                        <Accordion.Body>
                            {sortedEscalamientos.length > 0 ? (
                                <ListGroup variant="flush">
                                    {sortedEscalamientos.map((esc, index) => (
                                        <ListGroup.Item key={esc.escalamiento_id || index}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <strong>Nivel {esc.nivel}: {esc.nombre_completo}</strong>
                                                <Badge bg="info">{esc.puesto}</Badge>
                                            </div>
                                            <small className="text-muted">Teléfono: {esc.telefono}</small>
                                            {esc.correo && <small className="text-muted">Correo: {esc.correo}</small>}
                                            {esc.turno && <small className="text-muted">Turno: {esc.turno}</small>}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <p className="text-muted">No hay escalamientos configurados.</p>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="2">
                        <Accordion.Header>
                            Grupos de Mensajería ({protocoloData.grupos_mensajeria.length || 0})
                        </Accordion.Header>
                        <Accordion.Body>
                            {protocoloData.grupos_mensajeria?.length > 0 ? (
                                <ListGroup variant="flush">
                                    {protocoloData.grupos_mensajeria.map((group, index) => {
                                        const badgeBg = Number(group.tipo_id) === 1 ? "success"
                                            : Number(group.tipo_id) === 2 ? undefined : "secondary";
                                        const badgeStyle = Number(group.tipo_id) === 2
                                            ? { backgroundColor: '#23a9e8', color: 'white' }
                                            : {};

                                        return (
                                            <ListGroup.Item key={group.grupo_mensajeria_id || index}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <strong>{group.nombre_grupo}</strong>
                                                    <Badge bg={badgeBg} style={badgeStyle}>
                                                        {getTipoIdName(group.tipo_id)}
                                                    </Badge>
                                                </div>
                                                <small className="text-muted">
                                                    URL: <a
                                                        href={group.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#007bff' }}
                                                    >
                                                        {group.url}
                                                    </a>
                                                </small>
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            ) : (
                                <p className="text-muted">No hay grupos de mensajería configurados.</p>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Offcanvas.Body>
        </Offcanvas>
    );
};
